// The browser softphone: one JsSIP user agent registered straight to
// wss://sip.<domain>/ws (never through the app), with SIP and TURN settings
// from GET /v1/softphone-config.
//
// - Only one tab per browser holds the line (Web Locks), so tabs don't use up
//   the SIP account's device slots (max_contacts). Other tabs show
//   "open in another tab" and can take the line over.
// - TURN credentials last one hour: the config is fetched again 10 minutes
//   before iceExpiresAt, and before a call if it's close to expiry.
// - The INVITE goes out as soon as we have a usable path (relay candidate, or
//   1.5 s after gathering starts) instead of waiting for all of ICE gathering,
//   which can take many seconds when TURN/TLS is slow.
// - The SIP password is kept in memory only and never logged or shown.
// - Two lines at most: the call, and a second call ("consult") the user makes
//   while the first is on hold ("Talk first"). The user switches between them.
// - Transfer: "Transfer now" = the server sends the other person to the number;
//   "Talk first" + Complete joins the held call with the second one (both of
//   this device's calls end), Cancel goes back.
// - Conference (session 11): "Add now" calls a number into this call (no
//   hold); "Talk first" + Merge joins the second call into it. Up to 6 people;
//   the server keeps the list (call.conference live events), the user can
//   remove or mute people, leave (the others stay) or end it for everyone.
// - Every call has its own copy of the microphone track, so holding or muting
//   one line never silences the other.
// Calls are named to the server by their SIP Call-ID.

import { UA, WebSocketInterface } from 'jssip';
import type { IceCandidateEvent, RTCSession, RTCSessionEventMap } from 'jssip/lib/RTCSession';
import type { IncomingRequest } from 'jssip/lib/SIPMessage';
import type { RTCSessionEvent } from 'jssip/lib/UA';
import {
  ApiError,
  addToCall,
  completeTransfer,
  endCallForEveryone,
  getSoftphoneConfig,
  hangupLostCall,
  mergeCalls,
  muteInCall,
  removeFromCall,
  saveNumberName,
  saveSettings,
  transferCall,
  type CallerIdInfo,
  type ForwardInfo,
  type ForwardMode,
  type Participant,
  type SoftphoneConfig,
  type TransferInfo,
} from '../api';
import { dialTarget, withChoice } from './caller-id';
import type { LiveEvent } from '../events';
import { endText } from './end-text';
import { callForOtherTabs, type OtherTabCall } from './other-tab';
import { callerOf, displayNumber, normalizeNumber } from './number';
import { DEFAULT_BLOCKED_PREFIXES, checkNumber } from './number-rules';
import { computeQuality, readSample, type Quality, type StatsSample } from './quality';
import { peopleUpdate } from './people';
import { fixNullConnection } from './sdp';
import { Sounds } from './sounds';
import { isPermissionError, type MicState } from './browser-help';
import { alertAction, alertTag, callAlert, canShowAlertButtons, type AlertPermission } from './alerts';
import { getFlag, setFlag } from './device-prefs';
import { PROBE_MAX_AGE_MS, relayReachable } from './relay-probe';
import { HEARTBEAT_MS, MIN_RECONNECT_GAP_MS, tickAction, wakeAction, type Wake } from './keepalive';
import type { LineState } from './line-status';
import { audioLost, iceAction, isLostCause, newIceWatch, onIceState, onPackets, onSilence, relayOnly, type IceWatch } from './ice-recovery';
import { fullLabel, nicknameOf } from './number-names';

export type { LineState };
export type { OtherTabCall };

export type CallPhase = 'incoming' | 'connecting' | 'ringing' | 'connected' | 'ended';

/** PR 3b: what the phone says when a call can't start because the account is overdue. */
export const CALLS_PAUSED_TEXT = 'Calls are paused: your payment is overdue. Contact support to add credit.';

export interface CallView {
  id: number;
  direction: 'inbound' | 'outbound';
  number: string;
  name: string | null;
  phase: CallPhase;
  connectedAt: number | null; // Date.now() when audio connected
  muted: boolean;
  held: boolean;
  endText: string | null;
  quality: Quality | null;
  dtmf: string; // keys pressed during the call
  /** conference call: everyone in it (from the server), null when it's a two-party call */
  participants: Participant[] | null;
  /** stamp of the list shown (see people.ts) */
  peopleVersion: number;
  /** the network dropped and the audio path is being found again (see ice-recovery.ts) */
  reconnecting: boolean;
  /** incoming: which of the user's numbers was called (from the server; null if unknown) */
  to: string | null;
  /** the audio goes through the CloudPhone relay on purpose (the switch in Settings, or a recovery) */
  relayed: boolean;
  /** PR 3b: the plan minutes and credit run out in about a minute (the server said when) */
  limitSoon?: boolean;
}

/** Most people in one call, the user included (same limit as the server). */
export const MAX_PEOPLE = 6;

/** A call on the same line on another device or tab (from live events). */
export interface ElsewhereCall {
  callId: string;
  direction: 'inbound' | 'outbound';
  number: string;
  name: string | null;
  phase: 'ringing' | 'connected';
}

export interface PhoneState {
  line: LineState;
  lineMessage: string | null;
  call: CallView | null;
  elsewhere: ElsewhereCall | null;
  soundOn: boolean;
  notice: string | null; // a problem to show once (microphone blocked, ...)
  otherTabCall: OtherTabCall; // call in the tab holding the line (taking over would end it)
  /** "Calling from": the user's numbers and what outbound calls show (null until settings load) */
  callerId: CallerIdInfo | null;
  /** Do Not Disturb is on (incoming calls go straight to voicemail) */
  dnd: boolean;
  /** call forwarding (null until the settings are loaded) */
  forward: ForwardInfo | null;
  /** the second line: a call the user made while `call` waits on hold ("Talk first") */
  consult: CallView | null;
  /** what the second line is for: adding them to the call (Merge) or a transfer (Complete) */
  consultFor: 'add' | 'transfer' | null;
  /** which line the user is talking on (the other one is on hold) */
  talkingOn: 'call' | 'consult';
  /** what transfer this account can do (null until the settings are loaded) */
  transfer: TransferInfo | null;
  /** the browser says it has a network (navigator.onLine) */
  online: boolean;
  /** microphone permission for this site */
  mic: MicState;
  /** Date.now() of the last call that couldn't start because the microphone is blocked */
  micFailedAt: number | null;
  /** the browser had put this tab to sleep (Chrome/Edge memory saving) and reloaded it */
  wasAsleep: boolean;
  /** notification permission for this site (call alerts) */
  alerts: AlertPermission;
  /** this browser: "Not now" on the alerts card / the blocked banner was hidden */
  alertsNotNow: boolean;
  alertsBlockedHidden: boolean;
  /** "Turn on" was clicked but no choice came back: the browser may have hidden its question (Edge, Chrome "quieter" requests) */
  alertsQuestionHidden: boolean;
  /** this browser: Settings → Advanced "Always connect calls through the CloudPhone relay" */
  relayAlways: boolean;
  /** a relay (TURN) server is configured, so the switch can work */
  relayAvailable: boolean;
  /** with the switch on: the relay answered (true), didn't (false: calls use the normal path), not checked yet (null) */
  relayOk: boolean | null;
}

const REFRESH_BEFORE_MS = 10 * 60_000;
const MIC_NOT_ALLOWED = 'The microphone wasn’t allowed. Try again and choose Allow when the browser asks.';
const LOCK_NAME = 'cloudphone-line';
const ENDED_VISIBLE_MS = 2500;
/** how long a call alert waits for "to <number>" before showing without it */
const ALERT_WAIT_MS = 800;

export class Phone {
  private state: PhoneState = {
    line: 'starting',
    lineMessage: null,
    call: null,
    elsewhere: null,
    soundOn: false,
    otherTabCall: 'none',
    notice: null,
    callerId: null,
    dnd: false,
    forward: null,
    consult: null,
    consultFor: null,
    talkingOn: 'call',
    transfer: null,
    online: typeof navigator === 'undefined' || navigator.onLine !== false,
    mic: 'unknown',
    micFailedAt: null,
    wasAsleep: typeof document !== 'undefined' && (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true,
    alerts: notificationPermission(),
    alertsNotNow: getFlag('alertsNotNow'),
    alertsBlockedHidden: getFlag('alertsBlockedHidden'),
    alertsQuestionHidden: false,
    relayAlways: getFlag('relayAlways'),
    relayAvailable: false,
    relayOk: null,
  };
  private listeners = new Set<() => void>();
  private historyListeners = new Set<() => void>();
  private voicemailListeners = new Set<() => void>();

  readonly sounds = new Sounds();
  private audio: HTMLAudioElement;
  private ua: UA | null = null;
  private cfg: SoftphoneConfig | null = null;
  private cfgLoadedAt = 0;
  private session: RTCSession | null = null;
  /** the second line's call */
  private consultSession: RTCSession | null = null;
  /** call view id -> SIP Call-ID / the other side's audio / text to show when it ends */
  private sipCallIds = new Map<number, string>();
  private remoteStreams = new Map<number, MediaStream>();
  private endTexts = new Map<number, string>();
  /** calls the server said have ended (a call that fails at once can end before "on another device" is decided) */
  private endedCallIds: string[] = [];
  /** the second line's call the user ended or merged (goes away at once instead of showing "ended") */
  private cancelledConsult: number | null = null;
  private mergedConsult: number | null = null;
  /**
   * A conference being transferred: the user stays on it until the new person
   * joins (the server then hangs up this call, which ends as "Transferred to …").
   * Not answered, or the user stopped it: the ending text is taken back.
   */
  private confTransfer: { id: number; number: string; seen: boolean } | null = null;
  /** outgoing calls the server placed (only those can be transferred or turned into a conference) */
  private serverPlaced = new Set<number>();
  /** the microphone; each call sends its own clone of it (micOf) */
  private localStream: MediaStream | null = null;
  private micStreams = new Map<number, MediaStream>();
  private samples: StatsSample[] = [];
  private notification: Notification | null = null;
  /** service worker for alerts with Answer / Decline (Chrome, Edge); null where not available */
  private worker: ServiceWorkerRegistration | null = null;
  /** tag of the alert the service worker shows for the ringing call */
  private workerAlertTag: string | null = null;
  /** the worker couldn't show an alert: the page shows them itself from now on */
  private workerFailed = false;
  /** the call alert is waiting briefly for the number called (see alertIfRinging) */
  private alertTimer: ReturnType<typeof setTimeout> | null = null;
  private relayProbeAt = 0;
  private relayProbing = false;
  /** recent call.incoming live events: the number the caller dialled (to match with the ringing call) */
  private recentIncoming: { number: string; to: string; at: number }[] = [];
  private nextCallId = 1;
  /** PR 3b: the account's calls are paused (payment overdue): no call is started */
  private paused = false;
  private authRetried = false;
  /** SIP login changed during a call: rebuild the user agent when the call ends. */
  private rebuildPending = false;
  private running = false;
  /** caller ID choices being saved: a settings refresh meanwhile must not undo the new choice on screen */
  private callerIdSaving = 0;
  private dndSaving = 0;
  private forwardSaving = 0;
  /** keeping the registration alive (see keepalive.ts) */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastTick = Date.now();
  private lastRefreshAt = 0;
  private lastReconnectAt = 0;
  /** what the line says while a new user agent signs in ('updating' after a SIP login change) */
  private connectingAs: 'connecting' | 'updating' = 'connecting';
  private micPermission: PermissionStatus | null = null;
  /** per call: its audio connection and how getting it back is going */
  private ice = new Map<
    number,
    {
      pc: RTCPeerConnection;
      watch: IceWatch;
      timer: ReturnType<typeof setTimeout> | null;
      /** every 2 s: is audio arriving at all? (a dead path can still say "connected") */
      poll: ReturnType<typeof setInterval> | null;
      rx: number | null;
      rxAt: number;
    }
  >();
  /** Date.now() the server last answered us (REGISTER or an OPTIONS check): proves SIP works */
  private registeredAt = 0;
  private lastSipCheckAt = 0;

  private timers = new Set<ReturnType<typeof setTimeout>>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private clearEndedTimer: ReturnType<typeof setTimeout> | null = null;
  private historyTimer: ReturnType<typeof setTimeout> | null = null;
  private historyDue = 0;

  private lockGen = 0;
  private lockAbort: AbortController | null = null;
  private releaseLock: (() => void) | null = null;
  // Tabs tell each other whether the line holder has a call, so "Use this tab" can warn.
  private channel: BroadcastChannel | null = null;
  private holding = false;
  private lastCallSent: OtherTabCall | null = null;

  constructor() {
    this.audio = document.createElement('audio');
    this.audio.autoplay = true;
    this.audio.setAttribute('playsinline', '');
    this.audio.hidden = true;
    this.sounds.onChange(() => this.set({ soundOn: this.sounds.unlocked }));
    void this.watchMic();
    void this.watchAlerts();
  }

  // ── store (for React's useSyncExternalStore) ───────────────

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getState = (): PhoneState => this.state;

  /** Called when a new voicemail arrives (live event). */
  onVoicemailChange(fn: () => void): () => void {
    this.voicemailListeners.add(fn);
    return () => this.voicemailListeners.delete(fn);
  }

  /** Called a little after a call ends, when the call log has something new. */
  onHistoryChange(fn: () => void): () => void {
    this.historyListeners.add(fn);
    return () => this.historyListeners.delete(fn);
  }

  private set(patch: Partial<PhoneState>): void {
    this.state = { ...this.state, ...patch };
    if (this.holding) this.announce(false);
    this.listeners.forEach((f) => f());
  }

  private setCall(id: number, patch: Partial<CallView>): void {
    const c = this.state.call;
    if (c && c.id === id) return this.set({ call: { ...c, ...patch } });
    const k = this.state.consult;
    if (k && k.id === id) this.set({ consult: { ...k, ...patch } });
  }

  /** The call the user is talking on now. */
  private active(): { view: CallView; session: RTCSession } | null {
    const k = this.state.consult;
    if (this.state.talkingOn === 'consult' && k && k.phase !== 'ended' && this.consultSession) return { view: k, session: this.consultSession };
    const c = this.state.call;
    return c && this.session ? { view: c, session: this.session } : null;
  }

  /** The second line is gone: the user is back on the call (still on hold until they resume). */
  private oneLine(): Partial<PhoneState> {
    return { consult: null, consultFor: null, talkingOn: 'call' };
  }

  private isLive(id: number): boolean {
    return this.state.call?.id === id || this.state.consult?.id === id;
  }

  private later(fn: () => void, ms: number): ReturnType<typeof setTimeout> {
    const t = setTimeout(() => {
      this.timers.delete(t);
      fn();
    }, ms);
    this.timers.add(t);
    return t;
  }

  // ── life cycle ─────────────────────────────────────────────

  start(): void {
    if (this.running) return;
    this.running = true;
    if (!this.channel && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel('cloudphone-line');
      this.channel.onmessage = (e: MessageEvent<{ type: string; call?: OtherTabCall }>) => {
        if (e.data?.type === 'ask' && this.holding) this.announce(true);
        if (e.data?.type === 'status' && !this.holding) {
          const call = e.data.call === 'ringing' || e.data.call === 'connected' ? e.data.call : 'none';
          this.set({ otherTabCall: call });
        }
      };
    }
    if (!this.audio.isConnected) document.body.appendChild(this.audio);
    window.addEventListener('pagehide', this.onPageHide);
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('resume', this.onResume); // Chrome unfroze the tab
    // any click or key on the page lets the ringtone play (browsers need one)
    document.addEventListener('pointerdown', this.onInteract, true);
    document.addEventListener('keydown', this.onInteract, true);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.onFocus);
    void this.startWorker();
    this.lastTick = Date.now();
    if (!this.heartbeatTimer) this.heartbeatTimer = setInterval(this.heartbeat, HEARTBEAT_MS);
    this.requestLine('try');
  }

  /** Log out / leave: hang up, unregister, give the line back. start() may be called again. */
  stop(): void {
    this.running = false;
    window.removeEventListener('pagehide', this.onPageHide);
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('resume', this.onResume);
    document.removeEventListener('pointerdown', this.onInteract, true);
    document.removeEventListener('keydown', this.onInteract, true);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.onFocus);
    navigator.serviceWorker?.removeEventListener('message', this.onWorkerMessage);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    this.lockAbort?.abort();
    this.lockAbort = null;
    this.lockGen++;
    this.teardown();
    this.releaseLock?.();
    this.releaseLock = null;
    this.audio.remove();
    this.channel?.close();
    this.channel = null;
  }

  /** "Use this tab": take the line from the other tab (its call, if any, ends). */
  useHere(): void {
    if (this.state.line !== 'other_tab') return;
    void this.sounds.unlock();
    this.requestLine('steal');
  }

  private onPageHide = (): void => {
    // best effort: unregister so calls don't ring a closed tab
    try {
      this.ua?.stop();
    } catch {
      /* closing anyway */
    }
  };

  private onVisible = (): void => {
    if (document.visibilityState === 'hidden') {
      this.alertIfRinging(); // the user switched away while it rings
      return;
    }
    this.readAlerts();
    this.wake('visible');
  };

  private onInteract = (): void => {
    if (!this.sounds.unlocked) void this.sounds.unlock();
  };

  private onBlur = (): void => this.alertIfRinging();

  private onFocus = (): void => this.readAlerts();

  /**
   * The service worker (/sw.js) only shows call alerts with Answer / Decline
   * and passes the click back here; it caches nothing. Without it (old
   * browser, private window) alerts are shown by the page, click-to-open.
   */
  private async startWorker(): Promise<void> {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw) return;
    sw.addEventListener('message', this.onWorkerMessage);
    try {
      await sw.register('/sw.js', { scope: '/' });
      this.worker = await sw.ready;
    } catch (err) {
      log('alert buttons not available', { error: (err as Error).message });
    }
  }

  private onWorkerMessage = (e: MessageEvent): void => {
    const m = e.data as { type?: string; tag?: string; action?: unknown; error?: string } | null;
    if (!m?.type?.startsWith('cloudphone-alert')) return;
    if (m.type === 'cloudphone-alert-shown') return log('call alert shown', { buttons: true, hidden: document.hidden });
    if (m.type === 'cloudphone-alert-failed') {
      log('call alert failed', { buttons: true, error: m.error });
      this.workerFailed = true;
      if (m.tag === this.workerAlertTag) {
        this.workerAlertTag = null;
        this.alertIfRinging(false); // the page's own alert, without buttons
      }
      return;
    }
    if (m.type !== 'cloudphone-alert-click') return;
    const c = this.state.call;
    const action = alertAction(m.action);
    // only the alert of the call ringing now (an old alert clicked late does nothing but open the tab)
    const current = c?.phase === 'incoming' && m.tag === alertTag(c.id);
    log('call alert clicked', { action, current });
    if (m.tag === this.workerAlertTag) this.workerAlertTag = null;
    if (!current) return;
    if (action === 'answer') void this.answer();
    else if (action === 'decline') this.decline();
  };

  private onOnline = (): void => {
    this.set({ online: true });
    // During a call the user agent isn't replaced; just don't wait for JsSIP's
    // reconnect back-off (up to 30 s) before the call's audio can be restored.
    if (this.session || this.consultSession) this.nudgeTransport();
    this.wake('online');
  };

  /** Close the (dead) WebSocket and open a new one at once. */
  private reopenTransport(): void {
    try {
      const t = (this.ua as unknown as { _transport?: { connect(): void; disconnect(): void } } | null)?._transport;
      if (!t) return;
      t.disconnect();
      t.connect();
    } catch {
      /* JsSIP's own retry continues */
    }
  }

  /** Ask JsSIP to reconnect its WebSocket now (it does nothing if already connected or connecting). */
  private nudgeTransport(): void {
    try {
      const t = (this.ua as unknown as { _transport?: { connect(): void; isConnected(): boolean } } | null)?._transport;
      if (t && !t.isConnected()) t.connect();
    } catch {
      /* JsSIP's own retry continues */
    }
  }

  private onOffline = (): void => {
    this.set({ online: false });
  };

  private onResume = (): void => this.wake('resume');

  /** Every 20 s (or once a minute in a throttled hidden tab). */
  private heartbeat = (): void => {
    const now = Date.now();
    const gap = now - this.lastTick;
    this.lastTick = now;
    if (!this.running || !this.holding) return;
    const act = tickAction(gap, this.state.line, now - this.lastRefreshAt);
    if (act === 'wake') this.wake('sleep');
    else if (act === 'register') this.reRegister();
  };

  /** The laptop woke up, the network came back, or the tab is shown again: make sure the line still works. */
  private wake(why: Wake): void {
    if (!this.running || !this.holding) return;
    if (this.cfg) {
      // timers don't run while a laptop sleeps: renew settings if they ran out
      const exp = this.iceExpiresMs();
      if (exp !== null && exp - Date.now() < REFRESH_BEFORE_MS) void this.refreshConfig();
    }
    const inCall = Boolean(this.session || this.consultSession);
    if (!inCall) this.checkRelay(true); // another network may block or allow the relay
    const act = wakeAction(why, this.state.line, inCall, Date.now() - this.lastRefreshAt);
    if (act === 'register') this.reRegister();
    else if (act === 'restart') {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      void this.begin();
    } else if (act === 'reconnect') this.reconnect();
  }

  /** Send a REGISTER on the current connection (refreshes it; a dead connection then fails and reconnects). */
  private reRegister(): void {
    const ua = this.ua;
    if (!ua) return;
    this.lastRefreshAt = Date.now();
    try {
      if (ua.isConnected()) ua.register();
      else this.reconnect();
    } catch {
      /* the next tick tries again */
    }
  }

  /** A fresh connection and registration. Never while a call is up (it would end the call). */
  private reconnect(): void {
    if (!this.cfg || this.session || this.consultSession) return;
    const now = Date.now();
    if (now - this.lastReconnectAt < MIN_RECONNECT_GAP_MS) return;
    this.lastReconnectAt = now;
    this.lastRefreshAt = now;
    this.createUA(this.state.line === 'updating' ? 'updating' : 'connecting');
  }

  // One tab per browser holds the line.
  private requestLine(mode: 'try' | 'wait' | 'steal'): void {
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (!locks) {
      this.holding = true;
      void this.begin();
      return;
    }
    this.lockAbort?.abort();
    const gen = ++this.lockGen;
    const abort = new AbortController();
    this.lockAbort = abort;
    let granted = false;

    const opts: LockOptions =
      mode === 'try' ? { ifAvailable: true } : mode === 'steal' ? { steal: true } : { signal: abort.signal };

    locks
      .request(LOCK_NAME, opts, async (lock) => {
        if (!lock) {
          if (gen === this.lockGen) {
            this.set({ line: 'other_tab', lineMessage: null });
            this.channel?.postMessage({ type: 'ask' });
            this.requestLine('wait');
          }
          return;
        }
        if (gen !== this.lockGen) return;
        granted = true;
        this.holding = true;
        this.lastCallSent = null;
        this.set({ otherTabCall: 'none' });
        this.lockAbort = null;
        await this.begin();
        await new Promise<void>((resolve) => (this.releaseLock = resolve));
      })
      .catch(() => {
        // aborted by us (gen changed) or the line was taken by another tab
        if (granted && gen === this.lockGen && this.running) {
          this.teardown();
          this.set({ line: 'other_tab', lineMessage: null, call: null });
          this.channel?.postMessage({ type: 'ask' });
          this.requestLine('wait');
        }
      });
  }

  private async begin(): Promise<void> {
    if (!this.running) return;
    this.set({ line: 'starting', lineMessage: null });
    const ok = await this.loadConfig();
    if (ok && this.running) this.createUA();
  }

  private teardown(): void {
    if (this.holding) {
      this.channel?.postMessage({ type: 'status', call: 'none' });
      this.holding = false;
    }
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    this.rebuildPending = false;
    this.connectingAs = 'connecting';
    for (const t of [this.refreshTimer, this.retryTimer, this.clearEndedTimer, this.historyTimer]) if (t) clearTimeout(t);
    this.refreshTimer = this.retryTimer = this.clearEndedTimer = this.historyTimer = null;
    this.stopStats();
    this.sounds.stop();
    this.closeNotification();
    for (const id of [...this.ice.keys()]) this.dropIce(id);
    for (const s of [this.consultSession, this.session]) {
      if (!s) continue;
      try {
        s.terminate();
      } catch {
        /* already gone */
      }
    }
    this.session = null;
    this.consultSession = null;
    this.sipCallIds.clear();
    this.serverPlaced.clear();
    this.remoteStreams.clear();
    this.endTexts.clear();
    this.set(this.oneLine());
    this.stopLocalStream();
    if (this.ua) {
      (this.ua as unknown as { removeAllListeners(): void }).removeAllListeners();
      try {
        this.ua.stop();
      } catch {
        /* already stopped */
      }
      this.ua = null;
    }
    this.cfg = null;
  }

  /** Line holder: tell other tabs whether a call is in progress. */
  private announce(force: boolean): void {
    const call = callForOtherTabs(this.state.call, Boolean(this.session));
    if (!force && call === this.lastCallSent) return;
    this.lastCallSent = call;
    this.channel?.postMessage({ type: 'status', call });
  }

  // ── settings (SIP + TURN) ──────────────────────────────────

  private iceExpiresMs(): number | null {
    if (!this.cfg?.iceExpiresAt) return null;
    const t = Date.parse(this.cfg.iceExpiresAt);
    return Number.isFinite(t) ? t : null;
  }

  private async loadConfig(): Promise<boolean> {
    try {
      const cfg = await getSoftphoneConfig();
      this.cfg = cfg;
      this.cfgLoadedAt = Date.now();
      this.scheduleRefresh();
      this.set({ callerId: cfg.callerId ?? null, dnd: cfg.dnd === true, forward: cfg.forward ?? null, transfer: cfg.transfer ?? null, relayAvailable: this.hasTurn() });
      this.checkRelay(true);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'no_phone') {
        this.set({ line: 'no_phone', lineMessage: 'This account has no phone line yet. Ask your administrator to add one.' });
        return false;
      }
      if (err instanceof ApiError && err.status === 401) return false; // the app goes back to login
      this.set({ line: 'error', lineMessage: (err as Error).message || 'Couldn’t load your phone settings.' });
      this.retryTimer = setTimeout(() => void this.begin(), 10_000);
      return false;
    }
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const exp = this.iceExpiresMs();
    if (exp === null) return; // no TURN configured: nothing expires
    const wait = Math.max(exp - Date.now() - REFRESH_BEFORE_MS, 60_000);
    this.refreshTimer = setTimeout(() => void this.refreshConfig(), wait);
  }

  /** Fetch fresh settings. Rebuilds the user agent only if the SIP login itself changed. */
  private async refreshConfig(): Promise<void> {
    if (!this.running || !this.cfg) return;
    const old = this.cfg;
    try {
      const cfg = await getSoftphoneConfig();
      this.cfg = cfg;
      this.cfgLoadedAt = Date.now();
      this.scheduleRefresh();
      if (this.callerIdSaving === 0) this.set({ callerId: cfg.callerId ?? null });
      if (this.dndSaving === 0) this.set({ dnd: cfg.dnd === true });
      if (this.forwardSaving === 0) this.set({ forward: cfg.forward ?? null });
      this.set({ transfer: cfg.transfer ?? null, relayAvailable: this.hasTurn() });
      this.checkRelay(true); // new relay login
      const loginChanged =
        cfg.wssUrl !== old.wssUrl ||
        cfg.sipUri !== old.sipUri ||
        cfg.authorizationUser !== old.authorizationUser ||
        cfg.password !== old.password;
      if (loginChanged) this.rebuildUA('updating');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.code === 'no_phone')) return;
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => void this.refreshConfig(), 60_000);
    }
  }

  /** Before a call: make sure TURN credentials outlive the next few minutes. */
  private async ensureFresh(minSeconds: number): Promise<void> {
    const exp = this.iceExpiresMs();
    if (exp === null || exp - Date.now() > minSeconds * 1000) return;
    await this.refreshConfig();
  }

  /**
   * Calls on this device go through the relay from the start (Settings →
   * Advanced; needs a TURN server). Only once the relay has answered the
   * check: a relay-only call to a relay that can't be reached would hang
   * (relay-probe.ts), so until then calls use the normal path.
   */
  private relayFirst(): boolean {
    return this.state.relayAlways && this.hasTurn() && this.state.relayOk === true;
  }

  /** With the switch on: ask the relay whether it answers (background; at most one at a time). */
  private checkRelay(force = false): void {
    if (!this.state.relayAlways || !this.cfg || !this.hasTurn() || this.relayProbing) return;
    if (!force && Date.now() - this.relayProbeAt < PROBE_MAX_AGE_MS) return;
    this.relayProbing = true;
    const servers = this.cfg.iceServers;
    void relayReachable(servers).then((ok) => {
      this.relayProbing = false;
      this.relayProbeAt = Date.now();
      if (ok !== this.state.relayOk) log(ok ? 'relay reachable' : 'relay not reachable: calls use the normal path');
      this.set({ relayOk: ok });
    });
  }

  private pcConfig(): RTCConfiguration {
    return {
      iceServers: this.cfg?.iceServers ?? [],
      // direct first, TURN only when direct fails; relay only when the person chose it for this device
      iceTransportPolicy: this.relayFirst() ? 'relay' : 'all',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    };
  }

  // ── SIP user agent ─────────────────────────────────────────

  /**
   * Replace the user agent with one using the current settings. Stopping the
   * old one ends its calls, so during a call this waits until the call is over.
   */
  private rebuildUA(as: 'connecting' | 'updating' = 'connecting'): void {
    if (as === 'updating') this.connectingAs = 'updating';
    if (this.session || this.consultSession) {
      this.rebuildPending = true;
      return;
    }
    this.rebuildPending = false;
    this.createUA(this.connectingAs);
  }

  private createUA(as: 'connecting' | 'updating' = 'connecting'): void {
    const cfg = this.cfg;
    if (!cfg) return;
    this.connectingAs = as;
    this.lastRefreshAt = Date.now();
    if (this.ua) {
      (this.ua as unknown as { removeAllListeners(): void }).removeAllListeners();
      try {
        this.ua.stop();
      } catch {
        /* replaced */
      }
    }
    this.set({ line: as, lineMessage: null });

    const ua = new UA({
      sockets: [new WebSocketInterface(cfg.wssUrl)],
      uri: cfg.sipUri,
      authorization_user: cfg.authorizationUser,
      password: cfg.password,
      display_name: cfg.displayName,
      register: true,
      register_expires: 300,
      session_timers: false,
      user_agent: 'CloudPhone Web',
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 30,
    });
    this.ua = ua;
    const mine = () => this.ua === ua;

    ua.on('connecting', () => {
      if (mine() && this.state.line !== 'ready') this.set({ line: this.connectingAs, lineMessage: null });
    });
    ua.on('disconnected', () => {
      if (mine()) this.set({ line: 'offline', lineMessage: 'Connection lost. Reconnecting…' });
    });
    ua.on('connected', () => {
      if (mine()) this.recheckCalls();
    });
    ua.on('registered', () => {
      if (!mine()) return;
      this.registeredAt = Date.now();
      this.recheckCalls();
      this.authRetried = false;
      this.connectingAs = 'connecting';
      this.lastRefreshAt = Date.now();
      this.set({ line: 'ready', lineMessage: null });
    });
    ua.on('unregistered', () => {
      if (mine() && this.running) this.set({ line: 'offline', lineMessage: 'Not connected. Reconnecting…' });
    });
    ua.on('registrationFailed', (e: { cause?: string }) => {
      if (mine()) this.onRegistrationFailed(e.cause ?? 'unknown');
    });
    ua.on('newRTCSession', (e: RTCSessionEvent) => {
      if (mine() && e.originator === 'remote') this.onIncoming(e.session, e.request as IncomingRequest);
    });
    ua.start();
  }

  private onRegistrationFailed(cause: string): void {
    if (cause === 'Authentication Error' && !this.authRetried) {
      // the SIP password may have been changed: get the current one once
      this.authRetried = true;
      void (async () => {
        if (await this.loadConfig()) this.rebuildUA('updating');
      })();
      return;
    }
    if (cause === 'Connection Error' || cause === 'Request Timeout') {
      this.set({ line: 'offline', lineMessage: 'Can’t reach the phone service. Retrying…' });
    } else {
      this.set({
        line: 'failed',
        lineMessage:
          cause === 'Authentication Error'
            ? 'Your phone line refused the sign-in. Ask your administrator to check it.'
            : `Your phone line isn’t available (${cause}). Retrying…`,
      });
    }
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      if (this.ua && !this.ua.isRegistered()) this.ua.register();
    }, 30_000);
  }

  // ── calls ──────────────────────────────────────────────────

  get canCall(): boolean {
    return this.state.line === 'ready' && !this.state.call;
  }

  /**
   * Number rules (number-rules.ts): a phone number becomes 1 + 10 digits, or the
   * reason it can't be called. Codes with * or # (like *43) are left as they are.
   */
  checkDial(raw: string): { number: string } | { error: string } {
    const n = normalizeNumber(raw);
    if (!n) return { error: 'Enter a number.' };
    if (/[*#]/.test(n)) return { number: n };
    const r = checkNumber(n, this.cfg?.numberRules?.blockedPrefixes ?? DEFAULT_BLOCKED_PREFIXES);
    return r.ok ? { number: r.number } : { error: r.message };
  }

  /** PR 3b: the account's calls are paused (from GET /v1/me/readiness). */
  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  async call(raw: string): Promise<void> {
    if (!normalizeNumber(raw) || !this.canCall || !this.ua) return;
    if (this.paused) {
      this.set({ notice: CALLS_PAUSED_TEXT });
      return;
    }
    const checked = this.checkDial(raw);
    if ('error' in checked) {
      this.set({ notice: checked.error });
      return;
    }
    const number = checked.number;
    void this.sounds.unlock();
    this.checkRelay(); // for the next call, if the last check is old
    const id = this.nextCallId++;
    this.clearEnded();
    this.set({ call: this.newCall(id, 'outbound', number, null, 'connecting'), notice: null });

    await this.ensureFresh(120);
    const stream = await this.micOf(id);
    if (this.state.call?.id !== id) {
      this.releaseMic(id); // hung up while we waited
      if (!this.session && !this.consultSession) this.stopLocalStream();
      return;
    }
    if (!stream || !this.ua) {
      this.stopLocalStream();
      this.set({ call: null });
      return;
    }

    const handlers = this.sessionHandlers(id);
    // "Calling from": the server's rule applies the user's number (see caller-id.ts)
    const { target, headers } = dialTarget(number, this.cfg?.callerId, this.cfg?.transfer?.serverCalls === true);
    try {
      const s = this.ua.call(target, {
        extraHeaders: headers,
        mediaStream: stream,
        pcConfig: this.pcConfig(),
        rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
        eventHandlers: handlers,
      });
      this.sipCallIds.set(id, sipCallIdOf(s));
      if (target !== number && this.cfg?.transfer?.serverCalls) this.serverPlaced.add(id);
      // a call can fail synchronously inside ua.call(); don't keep a dead session
      if (this.state.call?.id === id && this.state.call.phase !== 'ended') this.session = s;
    } catch (err) {
      this.stopLocalStream();
      this.set({ call: null, notice: `Couldn’t start the call: ${(err as Error).message}` });
    }
  }

  /**
   * Pick the number outbound calls show (one of the user's numbers, or 'rotate').
   * Shown at once; saved for all the user's devices. Returns an error text, or null.
   */
  async chooseCallerId(choice: string | null): Promise<string | null> {
    const before = this.state.callerId;
    if (!before) return null;
    const optimistic = withChoice(before, choice);
    this.set({ callerId: optimistic });
    if (this.cfg) this.cfg = { ...this.cfg, callerId: optimistic };
    this.callerIdSaving++;
    try {
      const r = await saveSettings({ callerId: choice });
      this.set({ callerId: r.callerId });
      if (this.cfg) this.cfg = { ...this.cfg, callerId: r.callerId };
      return null;
    } catch (err) {
      this.set({ callerId: before });
      if (this.cfg) this.cfg = { ...this.cfg, callerId: before };
      return err instanceof ApiError ? err.message : 'Couldn’t change the number. Try again.';
    } finally {
      this.callerIdSaving--;
    }
  }

  /**
   * Numbers PR 2: name one of the user's numbers (null removes the name), for all their
   * devices (the others reload their numbers). Returns an error text, or null.
   */
  async setNumberName(number: string, nickname: string | null): Promise<string | null> {
    try {
      const r = await saveNumberName(number, nickname);
      this.set({ callerId: r.callerId });
      if (this.cfg) this.cfg = { ...this.cfg, callerId: r.callerId };
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Couldn’t save the name. Try again.';
    }
  }

  /** Turn Do Not Disturb on/off for all the user's devices. Returns an error text, or null. */
  /**
   * Save call forwarding (mode and/or number). Not optimistic: the server
   * checks the number. Returns an error text, or null. The other devices and
   * the "set up" state follow through sip.changed.
   */
  async setForwarding(change: { mode?: ForwardMode; number?: string | null }): Promise<string | null> {
    this.forwardSaving++;
    try {
      const r = await saveSettings({
        ...(change.mode !== undefined ? { forwardMode: change.mode } : {}),
        ...(change.number !== undefined ? { forwardNumber: change.number } : {}),
      });
      this.set({ forward: r.forward });
      if (this.cfg) this.cfg = { ...this.cfg, forward: r.forward };
      // the server sets it up within a second or two and says so (sip.changed);
      // check once more in case the live connection is down right now
      if (r.forward.mode !== 'off' && !r.forward.active) this.later(() => void this.refreshConfig(), 3000);
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Couldn’t save. Try again.';
    } finally {
      this.forwardSaving--;
    }
  }

  async setDnd(on: boolean): Promise<string | null> {
    const before = this.state.dnd;
    this.set({ dnd: on });
    this.dndSaving++;
    try {
      await saveSettings({ dnd: on });
      if (this.cfg) this.cfg = { ...this.cfg, dnd: on };
      return null;
    } catch (err) {
      this.set({ dnd: before });
      return err instanceof ApiError ? err.message : 'Couldn’t save. Try again.';
    } finally {
      this.dndSaving--;
    }
  }

  // ── second line, transfer and conference ──────────────────

  /** The call can be moved by the server (incoming, or an outgoing call the server placed). */
  private movable(c: CallView): boolean {
    return c.direction === 'inbound' || this.serverPlaced.has(c.id);
  }

  /** The user is on one connected call (no second line, nothing else in progress). */
  private oneConnectedCall(): CallView | null {
    const c = this.state.call;
    return c && c.phase === 'connected' && this.session && !this.state.consult ? c : null;
  }

  /**
   * Transfer is offered. A conference too (everyone stays with the new person,
   * the user leaves): needs room for one more and no transfer under way.
   */
  get canTransfer(): boolean {
    const c = this.oneConnectedCall();
    if (!this.state.transfer?.available || !c || !this.movable(c)) return false;
    if (!c.participants) return true;
    return Boolean(this.conferenceAvailable && peopleIn(c) < MAX_PEOPLE && !c.participants.some((p) => p.transfer));
  }

  /** "Talk first" (a second call while this one waits on hold): the server must place it. */
  get canTalkFirst(): boolean {
    return this.state.transfer?.serverCalls === true;
  }

  /** Conference calls are available on this account (shows "Add"). */
  get conferenceAvailable(): boolean {
    return this.state.transfer?.conference === true;
  }

  /** People can be added now (there is room for one more). */
  get canAdd(): boolean {
    const c = this.oneConnectedCall();
    return Boolean(this.conferenceAvailable && c && this.movable(c) && peopleIn(c) < MAX_PEOPLE);
  }

  /** Merge is offered while talking to the second line's person (who answered). */
  get canMerge(): boolean {
    const k = this.state.consult;
    return Boolean(this.conferenceAvailable && k && k.phase === 'connected' && this.consultSession && this.state.call?.phase === 'connected');
  }

  /**
   * "Add now": call the number into this call; nobody is put on hold. They
   * join when they answer (the call becomes a conference). Returns an error
   * text, or null.
   */
  async addNow(raw: string): Promise<string | null> {
    const c = this.state.call;
    const checked = this.checkDial(raw);
    if ('error' in checked) return checked.error;
    const number = checked.number;
    const sipCallId = c ? this.sipCallIds.get(c.id) : undefined;
    if (!c || !number || !sipCallId || !this.canAdd) return 'You can’t add someone to this call now.';
    try {
      const r = await addToCall(sipCallId, number);
      this.showPeople(c.id, r.participants, r.version);
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Couldn’t add them. You’re still on the call.';
    }
  }

  /**
   * "Talk first": hold this call and call the number on a second line, to add
   * them (then Merge) or to transfer to them (then Complete). Returns an error text, or null.
   */
  async talkFirst(raw: string, purpose: 'add' | 'transfer'): Promise<string | null> {
    const c = this.state.call;
    const checked = this.checkDial(raw);
    if ('error' in checked) return checked.error;
    const number = checked.number;
    const allowed = purpose === 'add' ? this.canAdd : this.canTransfer;
    if (!c || !number || !allowed || !this.canTalkFirst || !this.ua || !this.session) return 'You can’t make a second call now.';
    if (!c.held) this.session.hold();
    const id = this.nextCallId++;
    this.set({ consult: this.newCall(id, 'outbound', number, null, 'connecting'), consultFor: purpose, talkingOn: 'consult' });
    const stream = await this.micOf(id);
    if (!stream || !this.ua || this.state.consult?.id !== id) {
      this.releaseMic(id);
      if (this.state.consult?.id === id) this.set(this.oneLine());
      return 'Couldn’t start the call.';
    }
    const { target, headers } = dialTarget(number, this.cfg?.callerId, true);
    try {
      const s = this.ua.call(target, {
        extraHeaders: headers,
        mediaStream: stream,
        pcConfig: this.pcConfig(),
        rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
        eventHandlers: this.sessionHandlers(id),
      });
      this.sipCallIds.set(id, sipCallIdOf(s));
      this.serverPlaced.add(id);
      if (this.state.consult?.id === id && this.state.consult.phase !== 'ended') this.consultSession = s;
      return null;
    } catch (err) {
      this.releaseMic(id);
      this.set(this.oneLine());
      return `Couldn’t start the call: ${(err as Error).message}`;
    }
  }

  /** Two lines: talk on the other one (the one you leave goes on hold). */
  switchLine(): void {
    const c = this.state.call;
    const k = this.state.consult;
    if (!c || !k || !this.session || !this.consultSession || c.phase !== 'connected' || k.phase !== 'connected') return;
    const toConsult = this.state.talkingOn === 'call';
    const [leave, join] = toConsult ? [this.session, this.consultSession] : [this.consultSession, this.session];
    if (!leave.isOnHold().local) leave.hold();
    if (join.isOnHold().local) join.unhold();
    this.set({ talkingOn: toConsult ? 'consult' : 'call' });
    this.playAudioOf((toConsult ? k : c).id);
    this.startStats();
  }

  /**
   * Merge: the second line's person joins the call (which becomes, or already
   * is, a conference). The second line ends; the user is back on the call.
   */
  async merge(): Promise<string | null> {
    const c = this.state.call;
    const k = this.state.consult;
    const a = c ? this.sipCallIds.get(c.id) : undefined;
    const b = k ? this.sipCallIds.get(k.id) : undefined;
    if (!c || !k || !a || !b || !this.canMerge) return 'Wait until they answer.';
    this.mergedConsult = k.id; // its end (the server hangs it up) is part of the merge
    try {
      const r = await mergeCalls(a, b);
      this.showPeople(c.id, r.participants, r.version);
      if (this.session?.isOnHold().local) this.session.unhold();
      this.set({ talkingOn: 'call' });
      this.playAudioOf(c.id);
      return null;
    } catch (err) {
      this.mergedConsult = null;
      return err instanceof ApiError ? err.message : 'Couldn’t merge the calls. You’re still on both.';
    }
  }

  /**
   * "Transfer now": the other person is sent to `raw` and this device's call
   * ends. If the number doesn't answer, the call rings the user again.
   * Returns an error text, or null.
   */
  async transferNow(raw: string): Promise<string | null> {
    const c = this.state.call;
    const checked = this.checkDial(raw);
    if ('error' in checked) return checked.error;
    const number = checked.number;
    const sipCallId = c ? this.sipCallIds.get(c.id) : undefined;
    if (!c || !number || !sipCallId || !this.canTransfer) return 'This call can’t be transferred now.';
    const conference = Boolean(c.participants);
    // the server may hang this call up before its answer arrives: name the ending now
    this.endTexts.set(c.id, `Transferred to ${displayNumber(number)}`);
    if (conference) this.confTransfer = { id: c.id, number, seen: false };
    try {
      const r = await transferCall(sipCallId, number);
      if (this.isLive(c.id) && this.endTexts.has(c.id)) this.endTexts.set(c.id, `Transferred to ${displayNumber(r.to)}`);
      if (r.participants) this.showPeople(c.id, r.participants, r.version);
      if (conference && this.isLive(c.id)) this.set({ notice: `Calling ${displayNumber(r.to)}. You’ll leave the call when they answer.` });
      return null;
    } catch (err) {
      this.endTexts.delete(c.id);
      if (this.confTransfer?.id === c.id) this.confTransfer = null;
      return err instanceof ApiError ? err.message : 'The transfer didn’t go through. You’re still on the call.';
    }
  }

  /** Complete transfer: connect the held call with the second line's person; both of this device's calls end. */
  async completeTransfer(): Promise<string | null> {
    const c = this.state.call;
    const k = this.state.consult;
    const a = c ? this.sipCallIds.get(c.id) : undefined;
    const b = k ? this.sipCallIds.get(k.id) : undefined;
    if (!c || !k || k.phase !== 'connected' || !a || !b) return 'Wait until they answer.';
    // both calls may be hung up before the server's answer arrives: name the ending now
    const text = `Transferred to ${displayNumber(k.number)}`;
    this.endTexts.set(c.id, text);
    this.endTexts.set(k.id, text);
    // a conference: this call ends only once they have joined it
    if (c.participants) this.confTransfer = { id: c.id, number: k.number, seen: false };
    try {
      await completeTransfer(a, b);
      return null;
    } catch (err) {
      this.endTexts.delete(c.id);
      this.endTexts.delete(k.id);
      if (this.confTransfer?.id === c.id) this.confTransfer = null;
      return err instanceof ApiError ? err.message : 'The transfer didn’t go through. You’re still on the call.';
    }
  }

  /** End the second line's call (Cancel transfer / end that call) and go back to the call. */
  cancelConsult(): void {
    const s = this.consultSession;
    if (s) {
      this.cancelledConsult = this.state.consult?.id ?? null;
      try {
        s.terminate();
      } catch {
        /* already ending */
      }
    } else if (this.state.consult) {
      this.set(this.oneLine());
    }
    if (this.session?.isOnHold().local) this.session.unhold();
    this.set({ talkingOn: 'call' });
    this.playAudioOf(this.state.call?.id ?? -1);
  }

  /** Conference: end one person's part (a ringing person stops ringing). Returns an error text, or null. */
  async removePerson(id: string): Promise<string | null> {
    return this.manage((sip) => removeFromCall(sip, id), 'Couldn’t remove them. Try again.');
  }

  /** Conference: mute / unmute one person for everyone. Returns an error text, or null. */
  async mutePerson(id: string, muted: boolean): Promise<string | null> {
    return this.manage((sip) => muteInCall(sip, id, muted), 'Couldn’t change that. Try again.');
  }

  /** Conference: end the call for everyone (this device's call ends too). */
  async endForEveryone(): Promise<string | null> {
    const c = this.state.call;
    const sipCallId = c ? this.sipCallIds.get(c.id) : undefined;
    if (!c || !sipCallId || !c.participants) return null;
    try {
      await endCallForEveryone(sipCallId);
      return null;
    } catch (err) {
      // the server didn't end it: at least leave
      this.hangup();
      return err instanceof ApiError ? err.message : null;
    }
  }

  /** Show a list from the server unless a newer one is already shown. */
  private showPeople(id: number, participants: Participant[], version: number | undefined): void {
    const v = this.viewOf(id);
    const change = v ? peopleUpdate(v, participants, version) : null;
    if (change) {
      this.setCall(id, change);
      this.checkConfTransfer(id);
    }
  }

  private async manage(op: (sipCallId: string) => Promise<{ participants: Participant[]; version: number }>, fallback: string): Promise<string | null> {
    const c = this.state.call;
    const sipCallId = c ? this.sipCallIds.get(c.id) : undefined;
    if (!c || !sipCallId || !c.participants) return fallback;
    try {
      const r = await op(sipCallId);
      this.showPeople(c.id, r.participants, r.version);
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : fallback;
    }
  }

  async answer(): Promise<void> {
    const s = this.session;
    const c = this.state.call;
    if (!s || !c || c.phase !== 'incoming') return;
    void this.sounds.unlock();
    this.sounds.stop();
    this.closeNotification();
    this.setCall(c.id, { phase: 'connecting' });
    const stream = await this.micOf(c.id);
    if (this.session !== s) {
      this.releaseMic(c.id);
      return;
    }
    if (!stream) {
      // no microphone: keep it ringing (the person can fix it, answer elsewhere, or decline)
      if (this.state.call?.id === c.id && this.state.call.phase === 'connecting') {
        this.setCall(c.id, { phase: 'incoming' });
        this.sounds.ring();
      }
      return;
    }
    try {
      s.answer({ mediaStream: stream, pcConfig: this.pcConfig() });
    } catch (err) {
      this.set({ notice: `Couldn’t answer: ${(err as Error).message}` });
      s.terminate();
    }
  }

  /**
   * Decline a ringing call. With "ring all my devices" on, send 603 Decline:
   * the server then stops the call on every device (and the caller hears
   * busy). Without it, 486 Busy Here as before. 486 is also what this phone
   * sends on its own when it is already on a call, which must NOT stop the
   * user's other devices.
   */
  decline(): void {
    const s = this.session;
    if (!s || this.state.call?.phase !== 'incoming') return;
    if (this.cfg?.ringAll) s.terminate({ status_code: 603, reason_phrase: 'Decline' });
    else s.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
  }

  /**
   * The red button: ends the line the user is talking on. On the second line
   * that is that call (back to the first); in a conference the user leaves and
   * the others stay (endForEveryone ends it for all).
   */
  hangup(): void {
    if (this.state.consult && this.state.talkingOn === 'consult') return this.cancelConsult();
    const c = this.state.call;
    if (!c) return;
    if (!this.session) {
      // still waiting for the microphone: just drop it
      this.set({ call: null });
      return;
    }
    if (c.phase === 'incoming') return this.decline();
    // offline or the audio path is down: the BYE may not get through, so ask the server too
    if (!this.state.online || !this.ua?.isConnected() || this.ice.get(c.id)?.watch.badSince) this.reportLost(this.sipCallIds.get(c.id));
    try {
      this.session.terminate();
    } catch {
      this.finish(c.id, 'Call ended');
    }
  }

  toggleMute(): void {
    const a = this.active();
    if (!a || a.view.phase !== 'connected') return;
    if (a.view.muted) a.session.unmute({ audio: true });
    else a.session.mute({ audio: true });
  }

  /** Hold / resume the call (in a conference the others keep talking, without music). */
  toggleHold(): void {
    const s = this.session;
    const c = this.state.call;
    if (!s || !c || c.phase !== 'connected' || this.state.consult) return;
    if (c.held) s.unhold();
    else s.hold();
  }

  dtmf(key: string): void {
    const a = this.active();
    const s = a?.session;
    const c = a?.view;
    if (!s || !c || c.phase !== 'connected' || !/^[0-9*#]$/.test(key)) return;
    const sender = s.connection?.getSenders().find((x) => x.track?.kind === 'audio');
    const inBand = Boolean(sender?.dtmf?.canInsertDTMF);
    try {
      s.sendDTMF(key, { transportType: inBand ? 'RFC2833' : 'INFO', duration: 120, interToneGap: 70 } as never);
      this.setCall(c.id, { dtmf: (c.dtmf + key).slice(-24) });
    } catch {
      /* ignore a key pressed mid-renegotiation */
    }
  }

  dismissNotice(): void {
    this.set({ notice: null });
  }

  /** Hide "the browser put this tab to sleep". */
  dismissAsleep(): void {
    this.set({ wasAsleep: false });
  }

  /**
   * "Turn on" (alerts card, reminder line, Settings). Must run from a click:
   * lets the page play the ringtone, asks for notifications, and for the
   * microphone if the browser hasn't been asked yet (so answering is one click).
   */
  async enableAlerts(): Promise<void> {
    const unlocking = this.sounds.unlock();
    // ask first: Firefox and Safari only ask from a click, before anything is awaited
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        const t0 = Date.now();
        await Notification.requestPermission();
        // answered "default" at once = no question was shown: Edge (and Chrome's "quieter
        // notification requests") block new sites with a small icon in the address bar instead
        if (Notification.permission === 'default' && Date.now() - t0 < 1500) this.set({ alertsQuestionHidden: true });
      } catch {
        /* older browsers */
      }
    }
    this.readAlerts();
    // dismissed without choosing: show the reminder line instead of the card
    if (this.state.alerts === 'default') this.dismissAlertsCard();
    // the microphone question comes straight after; waiting for the sound to turn on
    // first (it can take seconds on Windows) delayed it by 6–8 s in Chrome
    if (this.state.mic !== 'granted' && this.state.mic !== 'denied' && !this.localStream && navigator.mediaDevices?.getUserMedia) {
      try {
        const st = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        st.getTracks().forEach((t) => t.stop());
        this.set({ mic: 'granted', micFailedAt: null });
      } catch {
        /* blocked or dismissed: the microphone banner / the first call says so */
      }
    }
    await unlocking;
  }

  /** "Not now" on the alerts card: a one-line reminder stays. */
  hideAlertsQuestionHelp(): void {
    this.set({ alertsQuestionHidden: false });
  }

  dismissAlertsCard(): void {
    setFlag('alertsNotNow', true);
    this.set({ alertsNotNow: true });
  }

  /** Hide the "call alerts are blocked" banner on this browser (Settings still shows how to fix it). */
  hideBlockedAlerts(): void {
    setFlag('alertsBlockedHidden', true);
    this.set({ alertsBlockedHidden: true });
  }

  /** Settings → Advanced: send this device's calls through the relay. Applies to the next call. */
  setRelayAlways(on: boolean): void {
    setFlag('relayAlways', on);
    this.set({ relayAlways: on, relayOk: on ? this.state.relayOk : null });
    if (on) this.checkRelay(true);
  }

  /** The notification permission may change in the browser's site settings at any time. */
  private async watchAlerts(): Promise<void> {
    try {
      const st = await navigator.permissions?.query({ name: 'notifications' as PermissionName });
      if (st) st.onchange = () => this.readAlerts();
    } catch {
      /* re-read on focus / visible instead */
    }
  }

  private readAlerts(): void {
    const alerts = notificationPermission();
    if (alerts !== this.state.alerts) this.set({ alerts, ...(alerts !== 'default' ? { alertsQuestionHidden: false } : {}) });
  }

  private newCall(id: number, direction: 'inbound' | 'outbound', number: string, name: string | null, phase: CallPhase): CallView {
    return { id, direction, number, name, phase, connectedAt: null, muted: false, held: false, endText: null, quality: null, dtmf: '', participants: null, peopleVersion: 0, reconnecting: false, to: null, relayed: this.relayFirst() };
  }

  private onIncoming(s: RTCSession, req: IncomingRequest): void {
    if (this.session || this.consultSession || (this.state.call && this.state.call.phase !== 'ended')) {
      s.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      return;
    }
    const uri = req.from?.uri as { user?: string } | undefined;
    const { number, name } = callerOf(uri?.user, req.from?.display_name);
    const id = this.nextCallId++;
    this.clearEnded();
    this.session = s;
    this.sipCallIds.set(id, (req as unknown as { call_id?: string }).call_id ?? sipCallIdOf(s));
    this.set({ call: { ...this.newCall(id, 'inbound', number, name, 'incoming'), to: this.calledNumber(number) }, elsewhere: null });
    const handlers = this.sessionHandlers(id);
    for (const [ev, fn] of Object.entries(handlers)) s.on(ev as keyof RTCSessionEventMap, fn as never);
    this.sounds.ring();
    this.alertIfRinging();
  }

  private sessionHandlers(id: number): Partial<RTCSessionEventMap> {
    let sawSrflx = false;
    // one gathering per offer/answer (an ICE restart gathers again with a new ready())
    let gathering: (() => void) | null = null;
    const owns = () => this.isLive(id);

    return {
      peerconnection: (e) => this.attachPeerConnection(id, e.peerconnection as unknown as RTCPeerConnection),
      // never let Asterisk read our offer/answer as "on hold" (see sdp.ts)
      sdp: (e) => {
        if (e.originator === 'local') e.sdp = fixNullConnection(e.sdp);
      },
      icecandidate: (e: IceCandidateEvent) => {
        // Send the offer/answer early instead of waiting for every candidate.
        const type = e.candidate.type ?? / typ (\w+)/.exec(e.candidate.candidate)?.[1];
        if (gathering !== e.ready) {
          gathering = e.ready;
          sawSrflx = false;
          this.later(e.ready, 1500);
        }
        if (type === 'srflx') sawSrflx = true;
        if (type === 'relay') {
          if (sawSrflx) e.ready();
          else this.later(e.ready, 300);
        }
      },
      progress: (e: { originator: string; response?: { status_code: number; body?: string } }) => {
        if (!owns() || e.originator !== 'remote') return;
        this.setCall(id, { phase: 'ringing' });
        // 180 without audio: play our own ringback; 183 = the network sends its own
        if (e.response?.status_code === 180 && !e.response.body) this.sounds.ringback();
        else this.sounds.stop();
      },
      accepted: () => {
        if (!owns()) return;
        this.sounds.stop();
        this.closeNotification();
        this.setCall(id, { phase: 'connected', connectedAt: Date.now() });
        this.startStats();
      },
      confirmed: () => {
        const v = this.state.call?.id === id ? this.state.call : this.state.consult?.id === id ? this.state.consult : null;
        if (v && v.phase !== 'connected') {
          this.setCall(id, { phase: 'connected', connectedAt: Date.now() });
          this.startStats();
        }
      },
      muted: () => this.setCall(id, { muted: true }),
      unmuted: () => this.setCall(id, { muted: false }),
      hold: (e: { originator: string }) => {
        if (e.originator === 'local') this.setCall(id, { held: true, quality: null });
      },
      unhold: (e: { originator: string }) => {
        if (e.originator === 'local') {
          this.samples = [];
          this.setCall(id, { held: false });
          this.playAudioOf(id);
        }
      },
      ended: (e) => {
        if (isLostCause(e.cause)) this.reportLost(this.sipCallIds.get(id));
        this.finish(id, isLostCause(e.cause) ? 'Call dropped: the connection was lost' : endText(e.cause, 'ended', this.viewOf(id)?.direction, e.message));
      },
      failed: (e) => this.finish(id, endText(e.cause, 'failed', this.viewOf(id)?.direction, e.message)),
    };
  }

  private viewOf(id: number): CallView | null {
    return this.state.call?.id === id ? this.state.call : this.state.consult?.id === id ? this.state.consult : null;
  }

  /** Play this call's audio (the one the user is talking on). */
  private playAudioOf(id: number): void {
    const stream = this.remoteStreams.get(id);
    if (stream && this.audio.srcObject !== stream) this.audio.srcObject = stream;
  }

  private attachPeerConnection(id: number, pc: RTCPeerConnection): void {
    const entry = { pc, watch: newIceWatch(), timer: null, poll: null as ReturnType<typeof setInterval> | null, rx: null as number | null, rxAt: Date.now() };
    this.ice.set(id, entry);
    pc.addEventListener('iceconnectionstatechange', () => this.checkIce(id));
    entry.poll = setInterval(() => void this.checkSilence(id), 2000);
    pc.addEventListener('track', (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.remoteStreams.set(id, stream);
      if (this.active()?.view.id !== id) return; // the line on hold stays silent
      if (this.audio.srcObject !== stream) this.audio.srcObject = stream;
      this.audio.play().catch(() => {
        this.set({ notice: 'Your browser blocked call audio. Click anywhere on the page to hear the call.' });
        const resume = () => {
          void this.audio.play();
          this.set({ notice: null });
        };
        document.addEventListener('click', resume, { once: true });
      });
    });
  }

  /**
   * The call's audio path changed state. After a network drop longer than a
   * few seconds it doesn't come back by itself: restart ICE with a re-INVITE
   * once SIP is connected again (see ice-recovery.ts).
   */
  private checkIce(id: number): void {
    const e = this.ice.get(id);
    if (!e || !this.isLive(id)) return;
    if (e.timer) clearTimeout(e.timer);
    e.timer = null;
    const state = e.pc.iceConnectionState;
    const before = e.watch.badSince;
    e.watch = onIceState(e.watch, state, Date.now());
    if (before === null && e.watch.badSince !== null) log('audio path lost', { call: id, state });
    if (before !== null && e.watch.badSince === null) log('audio path back', { call: id, restarts: e.watch.restarts });
    if (e.watch.badSince === null) {
      if (this.viewOf(id)?.reconnecting) this.setCall(id, { reconnecting: false });
      return;
    }
    // still bad: is audio arriving anyway? then nothing to fix (lossy network, not a dead path)
    void packetsReceived(e.pc).then((n) => {
      if (this.ice.get(id) !== e) return;
      const now = Date.now();
      if (n !== null) e.watch = onPackets(e.watch, n, now);
      const view = this.viewOf(id);
      const lost = audioLost(e.watch, now);
      if (view && view.phase === 'connected' && view.reconnecting !== lost) this.setCall(id, { reconnecting: lost });
      const session = this.state.call?.id === id ? this.session : this.state.consult?.id === id ? this.consultSession : null;
      const act = iceAction(e.watch, e.pc.iceConnectionState, now, {
        callUp: Boolean(session) && view?.phase === 'connected',
        sipConnected: Boolean(this.ua?.isConnected()),
        registeredAt: this.registeredAt,
      });
      if (act === 'check_sip' && now - this.lastSipCheckAt >= 5000) {
        // prove the connection works before sending the re-INVITE (the answer rechecks the calls)
        this.lastSipCheckAt = now;
        this.checkSip();
      } else if (act === 'restart' && session) {
        const relay = relayOnly(e.watch.restarts, this.hasTurn());
        if (relay && e.pc.getConfiguration().iceTransportPolicy !== 'relay') {
          try {
            e.pc.setConfiguration({ ...e.pc.getConfiguration(), iceTransportPolicy: 'relay' });
            this.setCall(id, { relayed: true });
          } catch {
            /* keep trying direct */
          }
        }
        if (this.iceRestart(id, session)) {
          e.watch = { ...e.watch, restarts: e.watch.restarts + 1, lastRestartAt: now };
          log('ICE restart sent', { call: id, n: e.watch.restarts, relay: e.pc.getConfiguration().iceTransportPolicy === 'relay' });
        }
      }
      if (this.ice.get(id) === e && e.watch.badSince !== null) e.timer = setTimeout(() => this.checkIce(id), 1000);
    });
  }

  /**
   * Send the ICE-restart re-INVITE. Uses JsSIP's re-INVITE directly instead of
   * renegotiate(), which would end the call on any error answer (491, 488 …),
   * and keeps a timeout / dead connection on this one re-INVITE from ending the
   * call (JsSIP's default): the next attempt simply tries again.
   */
  private iceRestart(id: number, session: RTCSession): boolean {
    type Internals = {
      isReadyToReOffer(): boolean;
      isEstablished(): boolean;
      _sendReinvite(o: unknown): void;
      onTransportError?: () => void;
      onRequestTimeout?: () => void;
      onDialogError?: () => void;
    };
    const s = session as unknown as Internals;
    try {
      if (!s.isEstablished() || !s.isReadyToReOffer()) return false;
    } catch {
      return false;
    }
    const keep = () => log('ICE restart re-INVITE got no answer; the call stays', { call: id });
    s.onTransportError = keep;
    s.onRequestTimeout = keep;
    s.onDialogError = keep;
    const restore = () => {
      delete s.onTransportError;
      delete s.onRequestTimeout;
      delete s.onDialogError;
    };
    const guard = setTimeout(restore, 40_000); // longer than SIP's 32 s transaction timeout
    try {
      s._sendReinvite({
        rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false, iceRestart: true },
        eventHandlers: {
          succeeded: () => {
            clearTimeout(guard);
            restore();
          },
          failed: (response?: { status_code?: number }) => {
            clearTimeout(guard);
            restore();
            const code = response?.status_code;
            log('ICE restart refused', { call: id, code });
            // the server no longer knows this call: it ended while we were offline
            if (code === 481 || code === 404 || code === 408) {
              try {
                session.terminate();
              } catch {
                /* already gone */
              }
            }
          },
        },
      });
      return true;
    } catch {
      clearTimeout(guard);
      restore();
      return false;
    }
  }

  /**
   * This device lost a call (connection error, or hung up while offline): its
   * BYE may never have reached the server, which would leave the other person
   * connected (and the call billed) in silence. Ask the server to hang it up;
   * retried for 2 minutes in case the network is still coming back.
   */
  private reportLost(sipCallId: string | undefined): void {
    if (!sipCallId) return;
    const until = Date.now() + 120_000;
    const attempt = async (): Promise<void> => {
      try {
        await hangupLostCall(sipCallId);
        log('lost call hung up on the server', { sipCallId });
      } catch (err) {
        const retry = !(err instanceof ApiError) || err.status >= 500 || err.status === 0;
        if (retry && Date.now() < until && this.running) setTimeout(() => void attempt(), 5000);
      }
    };
    void attempt();
  }

  /**
   * Does SIP work right now? Send an OPTIONS request: any answer from the
   * server proves it (a REGISTER refresh can't tell us: JsSIP only reports the
   * first registration). Not connected yet: JsSIP's reconnect brings 'connected'.
   */
  private checkSip(): void {
    const ua = this.ua;
    const host = this.cfg?.sipUri.replace(/^sips?:[^@]*@/, '').replace(/[;>].*$/, '');
    if (!ua || !host) return;
    if (!ua.isConnected()) {
      this.nudgeTransport();
      return;
    }
    // a WebSocket can look open long after the network died under it: no answer
    // in 6 s means it's dead, so open a new one (calls survive; only the socket changes)
    let gotAnswer = false;
    setTimeout(() => {
      if (!gotAnswer && this.ua === ua && (this.session || this.consultSession)) {
        log('SIP connection not answering; reconnecting it');
        this.reopenTransport();
      }
    }, 6000);
    try {
      const answered = () => {
        gotAnswer = true;
        if (this.ua !== ua) return;
        this.registeredAt = Date.now();
        this.recheckCalls();
      };
      (ua as unknown as { sendOptions(t: string, b: null, o: unknown): unknown }).sendOptions(`sip:${host}`, null, {
        eventHandlers: {
          succeeded: answered,
          failed: (e: { originator?: string }) => {
            if (e.originator === 'remote') answered(); // an error answer still proves the connection works
          },
        },
      } as never);
    } catch {
      /* next check in 5 s */
    }
  }

  /** A TURN server is configured (needed for a relay-only restart). */
  private hasTurn(): boolean {
    return (this.cfg?.iceServers ?? []).some((s) => [s.urls].flat().some((u) => typeof u === 'string' && /^turns?:/.test(u)));
  }

  /** SIP is back (new WebSocket): calls waiting for their audio can restart ICE now. */
  private recheckCalls(): void {
    for (const id of this.ice.keys()) this.checkIce(id);
  }

  private dropIce(id: number): void {
    const e = this.ice.get(id);
    if (e?.timer) clearTimeout(e.timer);
    if (e?.poll) clearInterval(e.poll);
    this.ice.delete(id);
  }

  /** Audio stopped on a connected call that isn't on hold: treat the path as lost even if ICE says "connected". */
  private async checkSilence(id: number): Promise<void> {
    const e = this.ice.get(id);
    if (!e) return;
    const n = await packetsReceived(e.pc);
    if (this.ice.get(id) !== e || n === null) return;
    const now = Date.now();
    if (e.rx === null || n > e.rx) {
      e.rx = n;
      e.rxAt = now;
      return;
    }
    const view = this.viewOf(id);
    // only the line the user is talking on: a line waiting on hold gets no audio by design
    const held = !view || view.phase !== 'connected' || view.held || this.active()?.view.id !== id;
    if (held) {
      e.rxAt = now; // the gap starts when the call is live again
      return;
    }
    const before = e.watch.badSince;
    e.watch = onSilence(e.watch, n, e.rxAt, now, false);
    if (before === null && e.watch.badSince !== null) {
      log('no audio for 6 s; finding a new audio path', { call: id, ice: e.pc.iceConnectionState });
      this.checkIce(id);
    }
  }

  private finish(id: number, rawText: string): void {
    if (!this.isLive(id)) return;
    this.dropIce(id);
    const transferred = this.endTexts.has(id);
    const text = this.endTexts.get(id) ?? rawText;
    this.endTexts.delete(id);
    if (this.confTransfer?.id === id) this.confTransfer = null;
    this.remoteStreams.delete(id);
    this.sipCallIds.delete(id);
    this.serverPlaced.delete(id);
    this.releaseMic(id);

    // the second line's call ended: back to the call (it stays on hold until resumed)
    if (this.state.consult?.id === id) {
      this.consultSession = null;
      this.stopStats();
      this.sounds.stop();
      const quiet = this.mergedConsult === id || this.cancelledConsult === id;
      if (this.mergedConsult === id) this.mergedConsult = null;
      if (this.cancelledConsult === id) this.cancelledConsult = null;
      if (transferred) {
        // the transfer was completed: one clean "Transferred to …" ending
        this.set(this.oneLine());
        const main = this.state.call;
        if (main && main.phase !== 'ended' && !this.session) this.setCall(main.id, { phase: 'ended', endText: text, quality: null });
        else if (main?.phase === 'ended') this.setCall(main.id, { endText: text });
      } else if (quiet) {
        // merged into the call, or the user ended it: it simply goes away
        this.set(this.oneLine());
        this.startStats();
      } else {
        // they hung up / didn't answer: show why for a moment
        this.set({ consult: { ...this.state.consult, phase: 'ended', endText: text, quality: null }, talkingOn: 'call' });
        this.later(() => {
          if (this.state.consult?.id === id) this.set(this.oneLine());
        }, ENDED_VISIBLE_MS);
      }
      this.playAudioOf(this.state.call?.id ?? -1);
      this.historyChanged(2500);
      return;
    }

    // the call ended while the user has a second line: that call goes on as the call
    // (not when the user completed the transfer: then the second call ends too)
    if (!transferred && this.state.consult && this.consultSession && this.state.consult.phase !== 'ended') {
      const left = this.state.call;
      this.session = this.consultSession;
      this.consultSession = null;
      this.set({
        call: this.state.consult,
        ...this.oneLine(),
        notice: left ? (left.participants ? 'The conference call ended.' : `${left.name || displayNumber(left.number)} hung up.`) : null,
      });
      this.playAudioOf(this.state.call?.id ?? -1);
      this.historyChanged(2500);
      return;
    }

    this.stopStats();
    this.sounds.stop();
    this.closeNotification();
    this.stopLocalStream();
    this.session = null;
    this.audio.srcObject = null;
    if (!this.consultSession) this.set(this.oneLine()); // else it ends on its own a moment later
    this.setCall(id, { phase: 'ended', endText: text, quality: null });
    if (this.rebuildPending) {
      // outside JsSIP's own event handler, so the old agent isn't stopped mid-callback
      this.later(() => {
        if (this.running && this.rebuildPending) this.rebuildUA();
      }, 0);
    }
    this.clearEndedTimer = setTimeout(() => {
      if (this.state.call?.id === id) this.set({ call: null });
    }, ENDED_VISIBLE_MS);
    this.historyChanged(2500);
  }

  private clearEnded(): void {
    if (this.clearEndedTimer) clearTimeout(this.clearEndedTimer);
    this.clearEndedTimer = null;
    if (this.state.call?.phase === 'ended') this.set({ call: null });
  }

  /**
   * Reload Recent calls after `delayMs`. A call.ended live event arrives once the
   * worker has saved the call, so it reloads almost at once; a call ending in
   * this tab waits a little longer in case that event never comes (then the
   * row comes from MagnusBilling's CDR). The earliest pending reload wins.
   */
  private historyChanged(delayMs: number): void {
    const due = Date.now() + delayMs;
    if (this.historyTimer && this.historyDue <= due) return;
    if (this.historyTimer) clearTimeout(this.historyTimer);
    this.historyDue = due;
    this.historyTimer = setTimeout(() => {
      this.historyTimer = null;
      this.historyListeners.forEach((f) => f());
    }, delayMs);
  }

  /**
   * This call's own copy of the microphone (opened once, cloned per call), so
   * holding or muting one line never silences the other: JsSIP mutes and holds
   * by disabling the call's track.
   */
  private async micOf(id: number): Promise<MediaStream | null> {
    const have = this.micStreams.get(id);
    if (have) return have;
    if (!this.localStream || this.localStream.getAudioTracks().every((t) => t.readyState === 'ended')) {
      this.localStream = await this.getMicrophone();
    }
    if (!this.localStream) return null;
    const copy = this.localStream.clone();
    this.micStreams.set(id, copy);
    return copy;
  }

  private releaseMic(id: number): void {
    this.micStreams.get(id)?.getTracks().forEach((t) => t.stop());
    this.micStreams.delete(id);
  }

  /**
   * Follow the microphone permission (Permissions API; Chrome, Edge, Safari,
   * recent Firefox). Without it, the state comes from getUserMedia's result.
   */
  private async watchMic(): Promise<void> {
    try {
      const st = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
      if (!st) return;
      this.micPermission = st;
      const apply = () => {
        const mic: MicState = st.state === 'granted' ? 'granted' : st.state === 'denied' ? 'denied' : 'prompt';
        // blocked: the banner says it, so drop a "wasn't allowed" note shown a moment earlier
        const notice = mic === 'denied' && this.state.notice === MIC_NOT_ALLOWED ? null : this.state.notice;
        this.set(mic === 'denied' ? { mic, notice } : { mic, micFailedAt: null });
      };
      apply();
      st.onchange = apply;
    } catch {
      /* not supported here (older Firefox): learn it from the first call */
    }
  }

  private async getMicrophone(): Promise<MediaStream | null> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.set({ notice: 'This browser can’t use a microphone here. Use a current Chrome, Edge, Firefox or Safari.' });
      return null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      if (this.state.mic !== 'granted' || this.state.micFailedAt) this.set({ mic: 'granted', micFailedAt: null });
      return stream;
    } catch (err) {
      const name = (err as DOMException).name;
      if (isPermissionError(name)) {
        // Dismissing the browser's question also lands here; the Permissions
        // API tells "blocked" apart from "not decided yet".
        // Firefox updates the permission a moment after the error ("Block" in its prompt).
        if (this.micPermission?.state === 'prompt') await new Promise((r) => setTimeout(r, 400));
        const asked = this.micPermission?.state;
        if (asked === 'prompt') {
          this.set({ notice: MIC_NOT_ALLOWED });
        } else {
          this.set({ mic: 'denied', micFailedAt: Date.now(), notice: null }); // the banner explains how to fix it
        }
        return null;
      }
      const notice =
        name === 'NotFoundError' || name === 'OverconstrainedError'
            ? 'No microphone found. Plug in a headset or microphone and try again.'
            : name === 'NotReadableError'
              ? 'Your microphone is being used by another app. Close it and try again.'
              : `Couldn’t open the microphone (${name || 'unknown error'}).`;
      this.set({ notice });
      return null;
    }
  }

  private stopLocalStream(): void {
    for (const id of [...this.micStreams.keys()]) this.releaseMic(id);
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
  }

  // ── call quality ───────────────────────────────────────────

  private startStats(): void {
    this.stopStats();
    this.samples = [];
    this.statsTimer = setInterval(async () => {
      const a = this.active();
      const pc = a?.session.connection as unknown as RTCPeerConnection | undefined;
      const c = a?.view;
      if (!pc || !c || c.phase !== 'connected') return;
      const sample = await readSample(pc);
      const now = this.viewOf(c.id);
      if (!sample || !now || this.active()?.view.id !== c.id) return;
      this.samples.push(sample);
      if (this.samples.length > 6) this.samples.shift();
      if (!now.held) this.setCall(c.id, { quality: computeQuality(this.samples) });
    }, 2000);
  }

  private stopStats(): void {
    if (this.statsTimer) clearInterval(this.statsTimer);
    this.statsTimer = null;
    this.samples = [];
  }

  // ── alerts ─────────────────────────────────────────────────

  /**
   * A ringing call while CloudPhone isn't in front (another tab, another
   * window, minimised): the browser's notification. It keeps the system's
   * notification sound on, which is heard even when the page's ringtone isn't
   * (e.g. a laptop just woken from sleep). Clicking it brings the tab forward.
   */
  private alertIfRinging(wait = true): void {
    const c = this.state.call;
    if (!c || c.phase !== 'incoming' || c.direction !== 'inbound') return;
    if (!document.hidden && document.hasFocus()) return;
    // one alert per call, never replaced: Edge on Windows dropped an alert that was
    // closed and shown again at once with the same tag (owner test: "shown" in the
    // console, nothing on screen)
    if (this.notification || this.workerAlertTag || this.alertTimer) return;
    if (notificationPermission() !== 'granted') {
      log('call alert not shown', { permission: notificationPermission() });
      return;
    }
    // "to <number>" comes with a live event that can arrive just after the call rings: wait a moment for it
    if (wait && !c.to && (this.state.callerId?.numbers.length ?? 0) > 1) {
      this.alertTimer = this.later(() => {
        this.alertTimer = null;
        this.alertIfRinging(false);
      }, ALERT_WAIT_MS);
      return;
    }
    // Numbers PR 2: "to Sales · +1 (201) 285-8570" when the number has a name
    const names = this.state.callerId?.names;
    const { title, body } = callAlert(c.name || displayNumber(c.number), c.to ? (nicknameOf(names, c.to) ? fullLabel(c.to, names) : displayNumber(c.to)) : null);
    const tag = alertTag(c.id);
    // Chrome / Edge: the service worker shows it with Answer / Decline
    const maxActions = (Notification as unknown as { maxActions?: number }).maxActions;
    if (this.worker?.active && canShowAlertButtons(maxActions, !this.workerFailed)) {
      this.workerAlertTag = tag;
      this.worker.active.postMessage({ type: 'cloudphone-alert', title, body, tag, actions: true });
      return;
    }
    try {
      const n = new Notification(title, { body, tag, requireInteraction: true, silent: false });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      n.onshow = () => log('call alert shown', { hidden: document.hidden });
      n.onerror = () => log('call alert refused by the browser or the system');
      this.notification = n;
    } catch (err) {
      // some mobile browsers only allow notifications from a service worker
      log('call alert failed', { error: (err as Error).message });
    }
  }

  /** The user's number the caller dialled, when they have more than one (from the call.incoming live event). */
  private calledNumber(from: string): string | null {
    if ((this.state.callerId?.numbers.length ?? 0) < 2) return null;
    const now = Date.now();
    this.recentIncoming = this.recentIncoming.filter((r) => now - r.at < 10_000);
    // used once: a later call from the same caller has its own event
    const i = this.recentIncoming.findIndex((r) => sameDigits(r.number, from));
    if (i < 0) return null;
    const [hit] = this.recentIncoming.splice(i, 1);
    return hit!.to;
  }

  /** call.incoming may arrive before or after the call itself rings here. */
  private noteCalledNumber(from: string, to: string): void {
    this.recentIncoming = [...this.recentIncoming.slice(-9), { number: from, to, at: Date.now() }];
    const c = this.state.call;
    if (c?.phase === 'incoming' && c.direction === 'inbound' && !c.to && sameDigits(c.number, from)) {
      const called = this.calledNumber(from);
      if (called) {
        this.setCall(c.id, { to: called });
        if (this.alertTimer) {
          // the alert was waiting for this: show it now
          clearTimeout(this.alertTimer);
          this.timers.delete(this.alertTimer);
          this.alertTimer = null;
          this.alertIfRinging(false);
        }
      }
    }
  }

  private closeNotification(): void {
    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
      this.timers.delete(this.alertTimer);
      this.alertTimer = null;
    }
    this.notification?.close();
    this.notification = null;
    const tag = this.workerAlertTag;
    this.workerAlertTag = null;
    if (tag && this.worker) {
      void this.worker
        .getNotifications({ tag })
        .then((list) => list.forEach((n) => n.close()))
        .catch(() => undefined);
    }
  }

  // ── live events (other devices / tabs) ─────────────────────

  handleLiveEvent(ev: LiveEvent): void {
    if (ev.type === 'sip.changed') {
      // new SIP password: load it now instead of waiting for a failed re-register
      void this.refreshConfig();
      return;
    }
    if (ev.type === 'voicemail.new' || ev.type === 'voicemail.changed') {
      this.voicemailListeners.forEach((f) => f());
      this.historyChanged(150);
      return;
    }
    if (!('callId' in ev)) return;
    if (ev.type === 'call.limit') {
      this.onLimit(ev);
      return;
    }
    if (ev.type === 'call.conference') {
      this.onConference(ev);
      return;
    }
    if (ev.type === 'call.forwarded' || ev.type === 'call.transfer') {
      this.historyChanged(150); // forward leg / blind transfer result: the history row changed
      return;
    }
    if (ev.type === 'call.ended') {
      this.historyChanged(150);
      this.endedCallIds = [...this.endedCallIds.slice(-19), ev.callId];
      if (this.state.elsewhere?.callId === ev.callId) this.set({ elsewhere: null });
      // Ring all: another device declined, so this one was stopped (SIP CANCEL,
      // which on its own reads as "Missed call").
      const c = this.state.call;
      if (ev.reason === 'declined' && c?.phase === 'ended' && c.direction === 'inbound' && c.endText === 'Missed call') {
        this.setCall(c.id, { endText: 'Declined on another device' });
      }
      return;
    }
    if (ev.type === 'call.answered') {
      const el = this.state.elsewhere;
      if (el?.callId === ev.callId) this.set({ elsewhere: { ...el, phase: 'connected' } });
      return;
    }
    if (ev.type === 'call.incoming' && ev.to) this.noteCalledNumber(ev.number, ev.to);
    // call.incoming / call.outgoing: if this tab isn't part of the call a moment
    // later, it's happening somewhere else.
    this.later(() => {
      if (this.session || this.consultSession || (this.state.call && this.state.call.phase !== 'ended')) return;
      if (this.state.elsewhere?.callId === ev.callId || this.endedCallIds.includes(ev.callId)) return;
      this.set({
        elsewhere: {
          callId: ev.callId,
          direction: ev.direction,
          ...callerOf(ev.number, ev.name),
          phase: ev.type === 'call.incoming' ? 'ringing' : 'connected',
        },
      });
    }, 1200);
  }

  /**
   * PR 3b: the server says when this call's plan minutes and credit run out. A minute before,
   * the call screen says so and one soft tone plays (the call then ends at that point).
   */
  private onLimit(ev: Extract<LiveEvent, { type: 'call.limit' }>): void {
    const ends = Date.parse(ev.endsAt);
    if (!Number.isFinite(ends)) return;
    const digits = (n: string) => n.replace(/\D/g, '').slice(-10);
    const want = digits(ev.number);
    const live = [this.state.call, this.state.consult].filter((c): c is CallView => Boolean(c && c.phase !== 'ended' && c.direction === ev.direction));
    const c = live.find((x) => want && digits(x.number) === want) ?? (live.length === 1 ? live[0] : undefined);
    if (!c) return;
    const id = c.id;
    const show = () => {
      if (!this.isLive(id)) return;
      this.setCall(id, { limitSoon: true });
      this.sounds.chime();
    };
    const wait = ends - (ev.noticeSec ?? 60) * 1000 - Date.now();
    if (wait <= 0) show();
    else this.later(show, wait);
  }

  /** Who is in a conference call now (from the server); a note when an added person didn't answer. */
  private onConference(ev: Extract<LiveEvent, { type: 'call.conference' }>): void {
    const id = [...this.sipCallIds].find(([, sip]) => sip === ev.sipCallId)?.[0];
    if (id === undefined || !this.isLive(id)) return;
    if (!ev.ended) this.showPeople(id, ev.participants, ev.version);
    if (ev.note) {
      const who = displayNumber(ev.note.number);
      const still = ev.note.transfer ? ' You’re still on the call.' : '';
      this.set({ notice: ev.note.result === 'busy' ? `${who} is busy.${still}` : `${who} didn’t answer.${still}` });
    }
  }

  /**
   * A conference transfer that won't happen any more (the new person didn't
   * answer, hung up, or the user removed them): this call's ending isn't
   * "Transferred to …" after all. While they're on the list it still is.
   */
  private checkConfTransfer(id: number): void {
    const t = this.confTransfer;
    if (!t || t.id !== id) return;
    const people = this.viewOf(id)?.participants;
    if (!people) return;
    if (people.some((p) => p.transfer)) {
      t.seen = true;
      return;
    }
    // a list from before the server added them: not a sign of anything yet
    if (!t.seen) return;
    // joined: the server takes the user out now; the list may already show them as a normal person
    if (people.some((p) => !p.you && p.state === 'joined' && sameDigits(p.number, t.number))) return;
    this.confTransfer = null;
    this.endTexts.delete(id);
  }

  /** For tests and diagnostics: how old the settings are (never the password). */
  get configAgeMs(): number {
    return this.cfg ? Date.now() - this.cfgLoadedAt : -1;
  }
}

/** The browser's notification permission ('unsupported' without the Notification API). */
function notificationPermission(): AlertPermission {
  if (typeof Notification === 'undefined') return 'unsupported';
  const p = Notification.permission;
  return p === 'granted' || p === 'denied' ? p : 'default';
}

function sameDigits(a: string, b: string): boolean {
  const d = (x: string) => x.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d(a) !== '' && d(a) === d(b);
}

/** People in the call, the user included (2 in a two-party call). */
export function peopleIn(c: Pick<CallView, 'participants'>): number {
  return c.participants ? c.participants.length : 2;
}

/** The SIP Call-ID of a JsSIP session (the server finds the call by it). */
function sipCallIdOf(s: RTCSession): string {
  const x = s as unknown as { _dialog?: { id?: { call_id?: string } }; _request?: { call_id?: string } };
  return x._dialog?.id?.call_id ?? x._request?.call_id ?? '';
}


/** Audio packets received on this connection so far (null if unknown). */
async function packetsReceived(pc: RTCPeerConnection): Promise<number | null> {
  try {
    let n: number | null = null;
    (await pc.getStats()).forEach((r: { type?: string; kind?: string; packetsReceived?: number }) => {
      if (r.type === 'inbound-rtp' && r.kind === 'audio' && typeof r.packetsReceived === 'number') n = (n ?? 0) + r.packetsReceived;
    });
    return n;
  } catch {
    return null;
  }
}

/** Connection events for support (browser console: filter "CloudPhone"). No secrets. */
function log(what: string, detail?: Record<string, unknown>): void {
  console.info(`[CloudPhone] ${new Date().toISOString().slice(11, 19)} ${what}`, detail ?? '');
}
